export const COUPON_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  EXPIRED: "expired",
  SCHEDULED: "scheduled",
};

export const COUPON_STATUS_LABELS = {
  [COUPON_STATUS.ACTIVE]: "Active",
  [COUPON_STATUS.INACTIVE]: "Inactive",
  [COUPON_STATUS.EXPIRED]: "Expired",
  [COUPON_STATUS.SCHEDULED]: "Scheduled",
};

export const COUPON_CATEGORIES = [
  "Dynamic",
  "REFERRAL",
  "RENEWAL",
  "bundlediscount",
];

export const COUPON_CATEGORY_LABELS = {
  Dynamic: "Dynamic",
  REFERRAL: "Referral",
  RENEWAL: "Renewal",
  bundlediscount: "Bundle Discount",
};

export const COUPON_CATEGORY_OPTIONS = COUPON_CATEGORIES.map((category) => ({
  id: category,
  label: COUPON_CATEGORY_LABELS[category] ?? category,
}));

export const COUPON_SORT_DIRECTION = {
  ASC: "asc",
  DESC: "desc",
};

export const COUPON_CREATED_ON_SORT_OPTIONS = [
  { id: COUPON_SORT_DIRECTION.DESC, label: "Newest First" },
  { id: COUPON_SORT_DIRECTION.ASC, label: "Oldest First" },
];

export const COUPON_LIST_STATUS_FILTER_OPTIONS = [
  { id: "all", label: "All Status" },
  { id: COUPON_STATUS.ACTIVE, label: "Active" },
  { id: COUPON_STATUS.SCHEDULED, label: "Scheduled" },
  { id: COUPON_STATUS.EXPIRED, label: "Expired" },
];

export const COUPON_LIST_CATEGORY_FILTER_OPTIONS = [
  { id: "all", label: "All Categories" },
  ...COUPON_CATEGORY_OPTIONS.map((option) => ({
    id: option.id,
    label: option.label,
  })),
];

export const COUPON_LIST_SEARCH_FETCH_LIMIT = 100;

export const ACTIVE_COUPONS_DEFAULT_OFFSET = 0;

export const ACTIVE_COUPONS_DEFAULT_LIMIT = 100;

export const COUPON_PREVIEW_NOT_SET = "Not set";

export const COUPON_LOCKED_IN_EDIT_FIELDS = [
  "code",
  "category",
  "effectiveDate",
  "discountType",
  "discountValue",
  "minCartValue",
  "totalCouponCount",
];

export const CREATE_COUPON_DEFAULT_VALUES = {
  code: "",
  category: "",
  remarks: "",
  discountType: "",
  discountValue: "",
  maxAllowedAmount: "",
  effectiveDate: "",
  expiryDate: "",
  totalCouponCount: "",
  isApplicableMultipleTimes: false,
  isApplicableForSingleUser: false,
  uamId: "",
  minimumPackageMrp: "",
  minimumCartMrp: "",
  selectAllPackages: false,
  packageIds: [],
  selectAllPlatforms: false,
  platformIds: [],
  platforms: [],
};

export const PLATFORM_UI_DESCRIPTIONS = {
  web: "Infinity Learn Website",
  android: "Infinity Learn Android Application",
  ios: "Infinity Learn iOS Application",
};

export const COUPON_VALIDATION = {
  MAX_COUPON_CODE_LENGTH: 100,
  MAX_COUPON_REMARKS_LENGTH: 100,
  COUPON_CODE_REGEX: "^[A-Za-z0-9][A-Za-z0-9_-]*$",
  MAX_ALLOWED_EFFECTIVE_DATE_OFFSET_DAYS: 90,
  MIN_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS: 1,
  MAX_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS: 370,
  MAX_DISCOUNT_DECIMAL_PLACES: 2,
  MIN_PERCENTAGE_DISCOUNT: 0,
  MAX_PERCENTAGE_DISCOUNT: 100,
  MIN_FLAT_DISCOUNT_AMOUNT: 0,
  MAX_FLAT_DISCOUNT_AMOUNT: 100000,
  MIN_TOTAL_COUPON_COUNT: 1,
  MAX_TOTAL_COUPON_COUNT: 200000,
  MIN_MRP_AMOUNT: 0,
  ALL_PACKAGES_ID: 0,
  ALL_PLATFORMS_ID: 1,
};

export const DISCOUNT_TYPES = {
  FLAT: "flat",
  PERCENTAGE: "percentage",
};

export const DISCOUNT_TYPE_OPTIONS = [
  { id: DISCOUNT_TYPES.FLAT, label: "Flat" },
  { id: DISCOUNT_TYPES.PERCENTAGE, label: "Percentage" },
];

export const COUPON_USAGE_OPERATIONS = {
  INCREASE: "INCREASE",
  DECREASE: "DECREASE",
};

export const COUPON_USAGE_OPERATION_OPTIONS = [
  { id: COUPON_USAGE_OPERATIONS.INCREASE, label: "Increase" },
  { id: COUPON_USAGE_OPERATIONS.DECREASE, label: "Decrease" },
];

// Platform IDs from Package Management API (sequence):
// 1 = ALL, 2 = android, 3 = iOS, 4 = web
export const PLATFORM_ENUMS = {
  ALL: { platformId: 1, key: "all", label: "All" },
  ANDROID: { platformId: 2, key: "android", label: "Android" },
  IOS: { platformId: 3, key: "ios", label: "iOS" },
  WEB: { platformId: 4, key: "web", label: "Web" },
};

export const PLATFORM_ENUM_OPTIONS = [
  PLATFORM_ENUMS.ANDROID,
  PLATFORM_ENUMS.IOS,
  PLATFORM_ENUMS.WEB,
];

export const ALLOWED_PLATFORM_IDS = PLATFORM_ENUM_OPTIONS.map(
  (platform) => platform.platformId,
);

export const PLATFORM_OPTIONS = [
  { id: "android", label: "Android" },
  { id: "ios", label: "iOS" },
  { id: "web", label: "Web" },
];

export const COUPON_FORM_STEPS = [
  {
    id: 1,
    label: "Basic Info",
    title: "Basic Information",
    subtitle: "Define coupon code, category, and remarks",
  },
  {
    id: 2,
    label: "Validity",
    title: "Validity",
    subtitle: "Set effective and expiry dates",
  },
  {
    id: 3,
    label: "Discount",
    title: "Discount",
    subtitle: "Configure discount type and value",
  },
  {
    id: 4,
    label: "Usage Limits",
    title: "Usage Limits",
    subtitle: "Set coupon usage restrictions",
  },
  {
    id: 5,
    label: "Eligibility",
    title: "Eligibility",
    subtitle: "Define minimum MRP requirements",
  },
  {
    id: 6,
    label: "Packages",
    title: "Packages",
    subtitle: "Select packages where this coupon can be applied",
  },
  {
    id: 7,
    label: "Platforms",
    title: "Platforms",
    subtitle: "Choose platforms where this coupon can be used",
  },
];

export const COUPON_FORM_VIEW_SUBTITLES = {
  1: "Coupon code, category, and remarks",
  2: "Active period and current status",
  3: "Discount type, value, and maximum cap",
  4: "Total usage and per-user restrictions",
  5: "Minimum package and cart MRP requirements",
  6: "Packages where this coupon applies",
  7: "Platforms where this coupon can be used",
};

export const CREATE_COUPON_API_FIELDS = [
  { key: "code", label: "Coupon Code", section: "Basic" },
  { key: "category", label: "Category", section: "Basic" },
  { key: "remarks", label: "Remarks", section: "Basic" },
  { key: "effectiveDate", label: "Effective Date", section: "Validity" },
  { key: "expiryDate", label: "Expiry Date", section: "Validity" },
  { key: "discountType", label: "Discount Type", section: "Discount" },
  { key: "discountValue", label: "Discount Value", section: "Discount" },
  {
    key: "maxAllowedAmount",
    label: "Max Allowed Amount",
    section: "Discount",
    conditional: "PERCENTAGE only",
  },
  { key: "totalCouponCount", label: "Total Coupon Count", section: "Usage" },
  {
    key: "isApplicableMultipleTimes",
    label: "Is Applicable Multiple Times",
    section: "Usage",
  },
  {
    key: "isApplicableForSingleUser",
    label: "Is Applicable For Single User",
    section: "Usage",
  },
  {
    key: "uamId",
    label: "UAM ID",
    section: "Usage",
    conditional: "single user only",
  },
  {
    key: "minimumPackageMrp",
    label: "Minimum Package MRP",
    section: "Eligibility",
  },
  {
    key: "minimumCartMrp",
    label: "Minimum Cart MRP",
    section: "Eligibility",
    optional: true,
  },
  {
    key: "selectAllPackages",
    label: "Select All Packages",
    section: "Packages",
  },
  {
    key: "packageIds",
    label: "Package IDs",
    section: "Packages",
    conditional: "when not all packages",
  },
  {
    key: "selectAllPlatforms",
    label: "Select All Platforms",
    section: "Platforms",
  },
  {
    key: "platformIds",
    label: "Platforms (Web / Android / iOS)",
    section: "Platforms",
    conditional: "when not all platforms",
  },
];

